/**
 * Stats Screen - Usage analytics (Simplified UX)
 *
 * Simplified to focus on core functionality:
 * - Clear display of key metrics
 * - Simple category breakdown
 * - Clean loading/error states
 * - Removed: premium features, complex charts, excessive analytics
 */
import { useEffect } from "react";
import { View, Text, ScrollView, RefreshControl, TouchableOpacity } from "react-native";
import { useRouter } from "expo-router";
import { useTranslation } from "@dramadetector/shared";
import { useResponsiveScreenPadding } from "../../components/layout/TabScreenWrapper";
import { useAnalysisStats } from "@dramadetector/api-client";
import { TierButton, defaultTheme } from "@dramadetector/ui";
import { useAuth } from "../../contexts/AuthContext";
import { GlassBackground, GlassPill, GlassCard } from "../../components/glass";
import { Logo } from "../../components/Logo";
import { ScreenSkeleton } from "../../components/skeleton/ScreenSkeleton";
import { captureEvent } from "../../lib/analytics";

const STAT_CARD_COLORS: Record<string, string> = {
  blue: '#eff6ff',
  purple: '#f5f3ff',
  green: '#f0fdf4',
  red: '#fef2f2',
};

// Simple stat card component
const StatCard = ({
  label,
  value,
  icon,
  color = "blue"
}: {
  label: string;
  value: string | number;
  icon: string;
  color?: "blue" | "purple" | "green" | "red";
}) => {
  return (
    <GlassCard style={{ backgroundColor: STAT_CARD_COLORS[color] }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View>
          <Text style={{ fontSize: 24, marginBottom: 4 }}>{icon}</Text>
          <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#111827' }}>{value}</Text>
          <Text style={{ fontSize: 14, color: '#4b5563' }}>{label}</Text>
        </View>
      </View>
    </GlassCard>
  );
};

// Category colors keyed by API raw key
const CATEGORY_COLORS: Record<string, string> = {
  safe: '#dcfce7',
  caution: '#fef9c3',
  risky: '#ffedd5',
  high_drama: '#fee2e2',
  extreme_drama: '#fce7f3',
};

const CATEGORY_TEXT_COLORS: Record<string, string> = {
  safe: '#166534',
  caution: '#854d0e',
  risky: '#9a3412',
  high_drama: '#991b1b',
  extreme_drama: '#9d174d',
};

// Translate a raw API category key using the history_item.category namespace
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateCategory = (t: any, key: string): string => {
  const translated = t(`history_item.category.${key}`);
  if (!translated || translated.startsWith('history_item.category.')) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return translated;
};

// Simple category breakdown component
const CategoryBreakdown = ({
  breakdown,
  emptyLabel,
  title,
  translateCategoryKey,
}: {
  breakdown: Record<string, number>;
  emptyLabel: string;
  title: string;
  translateCategoryKey: (key: string) => string;
}) => {
  const categories = Object.entries(breakdown)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5); // Show top 5 categories

  const total = Object.values(breakdown).reduce((sum, count) => sum + count, 0);

  if (categories.length === 0) {
    return (
      <GlassCard>
        <Text style={{ textAlign: 'center', color: '#4b5563' }}>
          {emptyLabel}
        </Text>
      </GlassCard>
    );
  }

  return (
    <GlassCard>
      <Text style={{ fontWeight: '600', color: '#111827', marginBottom: 16, textAlign: 'center' }}>
        {title}
      </Text>

      {categories.map(([category, count], index) => {
        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
        const bgColor = CATEGORY_COLORS[category] ?? '#f3f4f6';
        const textColor = CATEGORY_TEXT_COLORS[category] ?? '#1f2937';
        const isLast = index === categories.length - 1;
        const categoryLabel = translateCategoryKey(category);

        return (
          <View key={category} style={{ marginBottom: isLast ? 0 : 12 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <View style={{ backgroundColor: bgColor, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 999 }}>
                <Text style={{ fontSize: 12, fontWeight: '500', color: textColor }}>{categoryLabel}</Text>
              </View>
              <Text style={{ fontSize: 14, fontWeight: '600', color: '#374151' }}>
                {count} ({percentage}%)
              </Text>
            </View>

            <View style={{ backgroundColor: '#e5e7eb', height: 8, borderRadius: 999 }}>
              <View
                style={{ backgroundColor: '#3b82f6', height: 8, borderRadius: 999, width: `${percentage}%` }}
              />
            </View>
          </View>
        );
      })}
    </GlassCard>
  );
};

export default function StatsScreen() {
  const { t } = useTranslation('stats', { useSuspense: false });
  const { t: tHistory } = useTranslation('history', { useSuspense: false });
  const { bottomPadding } = useResponsiveScreenPadding();
  const router = useRouter();
  const { user, accessToken, loading: authLoading, isAnonymous } = useAuth();

  const { data, isLoading, error, refetch, isRefetching } = useAnalysisStats({
    userId: user?.id,
    enabled: !!user && !authLoading && !!accessToken,
  });

  // Anon-engagement funnel: pairs with Anon_Stats_CtaClicked.
  useEffect(() => {
    if (isAnonymous && user?.id) {
      captureEvent('Anon_Stats_Viewed', { source: 'stats' });
    }
  }, [isAnonymous, user?.id]);

  // Auth pending or data loading — show skeleton (not ActivityIndicator) per
  // ANON_AUTHED_INTEGRATION.md §PR checklist. Why: skeleton preserves layout
  // and reduces perceived latency during the ~200–500ms JWT fetch window.
  if (authLoading || isLoading) {
    return <ScreenSkeleton variant="stats" testID="stats-screen-skeleton" />;
  }

  // Error state
  if (error) {
    return (
      <GlassBackground>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <GlassCard style={{ maxWidth: 384 }}>
            <Text style={{ textAlign: 'center', color: '#dc2626', fontWeight: '600', marginBottom: 8 }}>
              {t('stats.errors.load_failed')}
            </Text>
            <Text style={{ textAlign: 'center', color: '#4b5563', fontSize: 14, marginBottom: 16 }}>
              {error.message}
            </Text>
            <TouchableOpacity
              onPress={() => refetch()}
              style={{ backgroundColor: '#3b82f6', borderRadius: 8, paddingVertical: 8, paddingHorizontal: 16 }}
            >
              <Text style={{ color: 'white', textAlign: 'center', fontWeight: '500' }}>
                {t('stats.errors.pull_to_retry')}
              </Text>
            </TouchableOpacity>
          </GlassCard>
        </View>
      </GlassBackground>
    );
  }

  // Empty state — anon users get "Analyze a message" CTA (no sign-up nudge
  // on empty, per spec §3.5); authed users get the standard empty copy.
  if (!data || data.totalAnalyses === 0) {
    return (
      <GlassBackground>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <GlassCard style={{ maxWidth: 384 }}>
            <Text style={{ textAlign: 'center', fontSize: 60, marginBottom: 16 }}>📊</Text>
            <Text style={{ textAlign: 'center', fontWeight: '600', color: '#111827', marginBottom: 8 }}>
              {t('stats.empty.title')}
            </Text>
            <Text style={{ textAlign: 'center', color: '#4b5563', fontSize: 14, marginBottom: isAnonymous ? 16 : 0 }}>
              {t('stats.empty.message')}
            </Text>
            {isAnonymous && (
              <TierButton
                variant="primary"
                size="md"
                fullWidth
                onPress={() => router.push('/(tabs)/')}
                testID="stats-anon-empty-cta-analyze"
              >
                {t('stats.anon.empty.cta_analyze' as never)}
              </TierButton>
            )}
          </GlassCard>
        </View>
      </GlassBackground>
    );
  }

  const topCategory = data.categoryBreakdown
    ? Object.entries(data.categoryBreakdown).sort(([, a], [, b]) => b - a)[0]?.[0]
    : undefined;

  return (
    <GlassBackground>
      <View className="flex-1">
        {/* Header */}
        <View className="items-center px-4 pt-4 pb-2">
          <Logo size="lg" style={{ marginBottom: 16 }} />
          <GlassPill translationKey="tabs.stats" size="lg" testID="stats-header" />
          <Text className="text-center text-gray-600 text-sm mt-2">
            {t('stats.subtitle')}
          </Text>
        </View>

        {/* Content */}
        <ScrollView
          className="flex-1"
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingBottom: bottomPadding + 16,
            paddingTop: 16
          }}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={refetch}
              tintColor={defaultTheme.brandPrimary}
              colors={[defaultTheme.brandPrimary]}
            />
          }
        >
          {/* Stats Cards */}
          <View style={{ gap: 16, marginBottom: 24 }}>
            <StatCard
              label={t('stats.labels.total_analyses')}
              value={data.totalAnalyses}
              icon="📝"
              color="blue"
            />

            <StatCard
              label={t('stats.labels.average_score')}
              value={(data.averageScore ?? 0).toFixed(1)}
              icon="⭐"
              color="purple"
            />

            {topCategory && (
              <StatCard
                label={t('stats.pattern_description')}
                value={translateCategory(tHistory, topCategory)}
                icon="🎯"
                color="green"
              />
            )}
          </View>

          {/* Category Breakdown */}
          {data.categoryBreakdown && Object.keys(data.categoryBreakdown).length > 0 && (
            <View style={{ marginBottom: 24 }}>
              <CategoryBreakdown
                breakdown={data.categoryBreakdown}
                emptyLabel={t('stats.category_chart.empty')}
                title={t('stats.category_breakdown')}
                translateCategoryKey={(key) => translateCategory(tHistory, key)}
              />
            </View>
          )}

          {/* Insights */}
          <GlassCard>
            <Text style={{ textAlign: 'center', color: '#4b5563', fontSize: 14, lineHeight: 20 }}>
              💡 <Text style={{ fontWeight: '600' }}>{t('stats.insights.tip_label')}:</Text> {t('stats.insights.lower_scores')}
            </Text>
          </GlassCard>

          {/* Anon retention CTA — retention-framed, source=anon_stats per
              ANON_AUTHED_INTEGRATION.md §Anti-patterns #7 (no paywall framing). */}
          {isAnonymous && (
            <GlassCard style={{ marginTop: 16 }}>
              <Text style={{ textAlign: 'center', fontWeight: '600', color: '#111827', marginBottom: 8 }}>
                {t('stats.anon.cta.headline' as never)}
              </Text>
              <Text style={{ textAlign: 'center', color: '#4b5563', fontSize: 14, marginBottom: 16 }}>
                {t('stats.anon.cta.body' as never)}
              </Text>
              <TierButton
                variant="primary"
                size="md"
                fullWidth
                onPress={() => {
                  captureEvent('Anon_Stats_CtaClicked', { source: 'stats' });
                  router.push('/(auth)/signup?source=anon_stats');
                }}
                testID="stats-anon-retention-cta"
              >
                {t('stats.anon.cta.button' as never)}
              </TierButton>
            </GlassCard>
          )}
        </ScrollView>
      </View>
    </GlassBackground>
  );
}
